import { Table } from 'antd';
import Column from 'antd/es/table/Column';
import { useHistory } from 'react-router-dom';

import { ActionCellMenu } from 'app/components/lists/ActionCellMenu';
import { dateTimeCell, dateTimeCell2 } from 'app/components/lists/cells';
import { BOOKING_STATUS } from 'app/typings/core/admin/bookings/enum/bookingStatus';
import type { UserBookingResponse } from 'app/typings/core/admin/users/userBooking';
import { sortDate } from 'app/utils/sort';
import { capitalizeFirstLetter } from 'app/utils/string';

import 'styles/pages/Users/user-details.scss';

const STATUS_FILTER_VALUES = [
  {
    value: BOOKING_STATUS.ACCEPTED,
    text: capitalizeFirstLetter(BOOKING_STATUS.ACCEPTED),
  },
  {
    value: BOOKING_STATUS.ABORTED,
    text: 'Rejected',
  },
  {
    value: BOOKING_STATUS.CANCELED,
    text: capitalizeFirstLetter(BOOKING_STATUS.CANCELED),
  },
];

const DEFAULT_STATUS_FILTER = [BOOKING_STATUS.ACCEPTED];

const handleStatusFilter = (
  value: string | number | boolean,
  userBooking: UserBookingResponse
) => value === userBooking.status;

const sortBookingsByCreatedAtField = (
  valueA: UserBookingResponse,
  valueB: UserBookingResponse
) => sortDate(valueA.createdAt, valueB.createdAt, 'ascend');

const sortBookingsByCheckinField = (
  valueA: UserBookingResponse,
  valueB: UserBookingResponse
) => sortDate(valueA.checkIn, valueB.checkIn, 'ascend');

type BookingLinkedProps = {
  bookings?: UserBookingResponse[];
  isLoading?: boolean;
};

export const BookingLinked: React.FC<BookingLinkedProps> = ({
  bookings,
  isLoading,
}: BookingLinkedProps) => {
  const history = useHistory();

  const handleActionClick = ({ isLegacy, code }: UserBookingResponse) => {
    history.push(`/bookings/${isLegacy ? `legacy/${code}` : code}`);
  };

  const handleRowClick = ({ isLegacy, code }: UserBookingResponse) => {
    return {
      onClick: () =>
        history.push(`/bookings/${isLegacy ? `legacy/${code}` : code}`),
    };
  };

  return (
    <div className="bloc" style={{ minWidth: '800px' }}>
      <div className="title">Bookings</div>
      <Table
        dataSource={bookings}
        pagination={false}
        rowKey="code"
        onRow={handleRowClick}
        loading={isLoading}
      >
        <Column className="strong" key="code" title="Code" dataIndex="code" />
        <Column
          key="date"
          title="Date"
          sorter={sortBookingsByCreatedAtField}
          defaultSortOrder="descend"
          dataIndex="createdAt"
          render={dateTimeCell2}
        />
        <Column key="hotelName" title="Hotel" dataIndex="hotelName" />
        <Column
          key="checkIn"
          sorter={sortBookingsByCheckinField}
          defaultSortOrder="descend"
          title="Check-in"
          render={(checkIn) => <div>{dateTimeCell('DD/MM/YYYY')(checkIn)}</div>}
          dataIndex="checkIn"
        />
        <Column
          filters={STATUS_FILTER_VALUES}
          className="strong"
          title="Status"
          key="status"
          dataIndex="status"
          defaultFilteredValue={DEFAULT_STATUS_FILTER}
          filterResetToDefaultFilteredValue
          onFilter={handleStatusFilter}
          filterMultiple
          render={(value) => (
            <div>
              {STATUS_FILTER_VALUES.find((status) => status.value === value)
                ?.text ?? ''}
            </div>
          )}
        />
        <Column
          key="actions"
          className="actions-column"
          onCell={() => ({ onClick: (e) => e.stopPropagation() })}
          render={(entity) => (
            <ActionCellMenu
              entity={entity}
              handleActionClick={handleActionClick}
            />
          )}
        />
      </Table>
    </div>
  );
};
